import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { closeModal } from '../actions/modal';
import { notificationActions } from '../components/Notification/redux/actions';
import Wizard from '../containers/Modal/Wizard';
import Strings from '../Strings';
import { actions } from '../pages/SuperUser/Patients/redux/actions';
import {
  UPDATE_PRIMARY_CARE_PROVIDER_RESULT,
  UPDATE_REFERRING_PROVIDER_RESULT,
} from '../pages/SuperUser/Patients/redux/constants';
import {
  externalProviderToFullDTO,
  getExternalProviderUpdateError,
  isExternalProviderReady,
} from '../utils/externalProvider';
import ExternalProviderField from './OnboardPatientWizard/ExternalProviderField';
import './OnboardPatientWizard/OnboardPatientWizard.scss';

export const EDIT_EXTERNAL_PROVIDER_MODAL_NAME = 'edit-external-provider';
export const EXTERNAL_PROVIDER_TYPES = {
  primaryCare: 'primaryCareProvider',
  referring: 'referringProvider',
};

function EditExternalProviderModal(props) {
  const { data, onCancel, showNotification, updatePrimaryCareProvider, updateReferringProvider } = props;
  const [provider, setProvider] = React.useState(data.provider);
  const [providerDraft, setProviderDraft] = React.useState(data.provider);
  const [isProviderAccepted, setIsProviderAccepted] = React.useState(Boolean(data.provider));
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightInvalidFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const isPrimaryCareProvider = data.providerType === EXTERNAL_PROVIDER_TYPES.primaryCare;
  const providerLabel = isPrimaryCareProvider ? Strings.primaryCareProvider : Strings.referringProvider;

  React.useEffect(() => {
    if (isExternalProviderReady(providerDraft)) {
      setValidationErrors([]);
      return;
    }
    setValidationErrors([
      {
        property: 'externalProvider',
        propertyName: providerLabel,
        errors: [
          isProviderAccepted
            ? Strings.externalProviderDirectory.requiredProviderData
            : Strings.formatString(Strings.externalProviderDirectory.selectProviderRequired, providerLabel),
        ],
        informational: !isProviderAccepted,
        missing: !isProviderAccepted,
      },
    ]);
  }, [isProviderAccepted, providerDraft, providerLabel]);

  const onSubmit = () => {
    if (!isExternalProviderReady(providerDraft)) {
      setHighlightInvalidFields(true);
      return;
    }

    setIsSaving(true);
    const request = isPrimaryCareProvider
      ? updatePrimaryCareProvider(data.patientId, externalProviderToFullDTO(providerDraft))
      : updateReferringProvider(data.enrollmentId, externalProviderToFullDTO(providerDraft));
    const successType = isPrimaryCareProvider ? UPDATE_PRIMARY_CARE_PROVIDER_RESULT : UPDATE_REFERRING_PROVIDER_RESULT;

    request
      .then(response => {
        const errorMessage = getExternalProviderUpdateError(response, successType, Strings.errors.unknownError);
        if (errorMessage) {
          showNotification(errorMessage, 5000, true);
          setIsSaving(false);
          return;
        }

        showNotification(Strings.formatString(Strings.externalProviderDirectory.providerSaved, providerLabel));
        data.onSuccess?.();
        onCancel();
      })
      .catch(error => {
        showNotification(
          getExternalProviderUpdateError(error, successType, Strings.errors.unknownError),
          5000,
          true,
        );
        setIsSaving(false);
      });
  };

  const pages = [
    {
      id: 'edit-external-provider-page',
      title: Strings.formatString(
        data.provider
          ? Strings.externalProviderDirectory.changeProviderTitle
          : Strings.externalProviderDirectory.addProviderTitle,
        providerLabel,
      ),
      content: (
        <ExternalProviderField
          idPrefix="externalProvider"
          label={providerLabel}
          patientId={data.patientId}
          value={provider}
          onChange={updatedProvider => {
            setProvider(updatedProvider);
          }}
          onDraftChange={setProviderDraft}
          onAcceptanceChange={setIsProviderAccepted}
          required
          highlightInvalid={highlightInvalidFields}
          validationErrors={validationErrors}
        />
      ),
      emptyFieldsCount: validationErrors.filter(error => error.missing).length,
      canGoNext: !isSaving && isExternalProviderReady(providerDraft),
    },
  ];

  return (
    <Wizard
      name={EDIT_EXTERNAL_PROVIDER_MODAL_NAME}
      pages={pages}
      onSubmit={onSubmit}
      onNextButtonHover={setHighlightInvalidFields}
    />
  );
}

EditExternalProviderModal.propTypes = {
  data: PropTypes.shape({
    providerType: PropTypes.oneOf(Object.values(EXTERNAL_PROVIDER_TYPES)).isRequired,
    provider: PropTypes.object,
    patientId: PropTypes.string.isRequired,
    enrollmentId: PropTypes.string,
    onSuccess: PropTypes.func,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  updatePrimaryCareProvider: PropTypes.func.isRequired,
  updateReferringProvider: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  onCancel: () => dispatch(closeModal(EDIT_EXTERNAL_PROVIDER_MODAL_NAME)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  updatePrimaryCareProvider: (patientId, provider) => dispatch(actions.updatePrimaryCareProvider(patientId, provider)),
  updateReferringProvider: (enrollmentId, provider) =>
    dispatch(actions.updateReferringProvider(enrollmentId, provider)),
});

export default connect(null, mapDispatchToProps)(EditExternalProviderModal);
